// Static site data (navigation, services, wallets). Fully multilingual (de/it/en).
// German (de) is the primary language and the fallback. Proper nouns and product
// names (Ledger, MetaMask, Trezor, Bitcoin, wallet.dat, TXID…) are kept as-is.
import {
  KeyRound, Puzzle, FileWarning, Scale, Cpu, Landmark, ShieldCheck,
  Lock, MessagesSquare, SearchCheck,
} from "lucide-react";

// Resolve a {de,it,en} node for the active language, falling back to de then it.
const L = (lang, node) => {
  if (node == null) return "";
  if (typeof node === "string") return node;
  return node[lang] ?? node.de ?? node.it ?? "";
};

const WALLET_TYPES = {
  hardware: { de: "Hardware-Wallet", it: "Hardware wallet", en: "Hardware wallet" },
  software: { de: "Software-Wallet", it: "Wallet software", en: "Software wallet" },
  legacy: { de: "Legacy-Wallet", it: "Wallet legacy", en: "Legacy wallet" },
};

// ---------------------------------------------------------------------------
// SERVICES (cards used on Home, Servizi, header/footer dropdowns)
// ---------------------------------------------------------------------------
const SERVICES_DATA = [
  {
    slug: "recupero-password",
    icon: KeyRound,
    title: { de: "Passwort-Wiederherstellung", it: "Recupero password", en: "Password recovery" },
    short: {
      de: "Unterstützung, wenn das Wallet-Passwort vergessen wurde, aber Hinweise oder Backups vorhanden sind.",
      it: "Assistenza quando la password del wallet è dimenticata ma esistono indizi o backup.",
      en: "Assistance when the wallet password is forgotten but clues or backups exist.",
    },
    cases: [
      { de: "Teilweise erinnertes Passwort", it: "Password parzialmente ricordata", en: "Partially remembered password" },
      { de: "Bekannte Passwortvarianten", it: "Variazioni note della password", en: "Known password variations" },
      { de: "Wallet-Datei-Backup vorhanden", it: "Backup del file wallet disponibile", en: "Wallet file backup available" },
    ],
  },
  {
    slug: "seed-phrase-incompleta",
    icon: Puzzle,
    title: { de: "Rekonstruktion unvollständiger Seed-Phrasen", it: "Ricostruzione di seed phrase incomplete", en: "Incomplete seed phrase reconstruction" },
    short: {
      de: "Technische Analyse, wenn Wörter fehlen oder die Reihenfolge falsch ist.",
      it: "Analisi tecnica quando mancano alcune parole o l'ordine non è corretto.",
      en: "Technical analysis when some words are missing or the order is wrong.",
    },
    cases: [
      { de: "1–3 Wörter fehlen", it: "Mancano 1-3 parole", en: "1–3 words missing" },
      { de: "Falsche Wortreihenfolge", it: "Ordine delle parole errato", en: "Wrong word order" },
      { de: "Ungültiges Wort in der Phrase", it: "Parola non valida nella frase", en: "Invalid word in the phrase" },
    ],
  },
  {
    slug: "wallet-danneggiati",
    icon: FileWarning,
    title: { de: "Wiederherstellung beschädigter oder Legacy-Wallets", it: "Recupero di wallet danneggiati o legacy", en: "Recovery of damaged or legacy wallets" },
    short: {
      de: "Hilfe bei beschädigten Wallet-Dateien und nicht mehr unterstützter Software.",
      it: "Supporto per file wallet corrotti e software non più supportato.",
      en: "Support for corrupted wallet files and no-longer-supported software.",
    },
    cases: [
      { de: "Beschädigte wallet.dat-Datei", it: "File wallet.dat corrotto", en: "Corrupted wallet.dat file" },
      { de: "Veraltete Wallet-Software", it: "Software wallet obsoleto", en: "Obsolete wallet software" },
      { de: "Nicht öffenbares verschlüsseltes Backup", it: "Backup cifrato non apribile", en: "Encrypted backup that won't open" },
    ],
  },
  {
    slug: "eredita-digitale",
    icon: Scale,
    title: { de: "Digitaler Nachlass und professionelle Unterstützung", it: "Eredità digitale e supporto professionale", en: "Digital inheritance and professional support" },
    short: {
      de: "Hilfe für Erben und Fachleute mit angemessener rechtlicher Dokumentation.",
      it: "Assistenza per eredi e professionisti con adeguata documentazione legale.",
      en: "Assistance for heirs and professionals with appropriate legal documentation.",
    },
    cases: [
      { de: "Nachlass mit Dokumentation", it: "Successione con documentazione", en: "Succession with documentation" },
      { de: "Rechtliches oder notarielles Mandat", it: "Mandato legale o notarile", en: "Legal or notarial mandate" },
      { de: "Unterstützung für Unternehmen", it: "Supporto per aziende", en: "Support for companies" },
    ],
  },
];

// ---------------------------------------------------------------------------
// SERVICE detail pages
// ---------------------------------------------------------------------------
const SERVICE_PAGES_DATA = {
  "recupero-password": {
    icon: KeyRound,
    title: { de: "Wallet-Passwort-Wiederherstellung", it: "Recupero password del wallet", en: "Wallet password recovery" },
    intro: {
      de: "Wir unterstützen verifizierte Inhaber, wenn das Zugangspasswort zum Wallet vergessen wurde, aber noch für die Analyse nützliche Elemente vorhanden sind.",
      it: "Assistiamo i titolari verificati quando la password di accesso al wallet è stata dimenticata ma esistono ancora elementi utili all'analisi.",
      en: "We assist verified owners when the wallet access password has been forgotten but useful elements for the analysis still exist.",
    },
  },
  "seed-phrase-incompleta": {
    icon: Puzzle,
    title: { de: "Unvollständige Seed-Phrase", it: "Seed phrase incompleta", en: "Incomplete seed phrase" },
    intro: {
      de: "Wir analysieren Fälle, in denen die Wiederherstellungsphrase unvollständig ist oder Wörter in falscher Reihenfolge stehen – ohne jemals die vollständige Phrase zu verlangen.",
      it: "Analizziamo i casi in cui la frase di recupero è incompleta o con parole in ordine errato, sempre senza richiedere la frase completa.",
      en: "We analyze cases where the recovery phrase is incomplete or has words in the wrong order, never requesting the complete phrase.",
    },
  },
  "wallet-danneggiati": {
    icon: FileWarning,
    title: { de: "Beschädigte Wallets", it: "Wallet danneggiati", en: "Damaged wallets" },
    intro: {
      de: "Wir bearbeiten beschädigte Wallet-Dateien und nicht öffenbare Backups und bewerten die technische Machbarkeit von Fall zu Fall.",
      it: "Interveniamo su file wallet corrotti e backup non apribili, valutando la fattibilità tecnica caso per caso.",
      en: "We work on corrupted wallet files and backups that won't open, assessing technical feasibility case by case.",
    },
  },
  "wallet-obsoleti": {
    icon: Cpu,
    title: { de: "Veraltete und Legacy-Wallets", it: "Wallet obsoleti e legacy", en: "Obsolete and legacy wallets" },
    intro: {
      de: "Unterstützung für nicht mehr gepflegte Wallet-Software oder historische, nicht mehr unterstützte Formate.",
      it: "Supporto per software di wallet non più mantenuti o formati storici non più supportati.",
      en: "Support for no-longer-maintained wallet software or historical, unsupported formats.",
    },
  },
  "hardware-wallet": {
    icon: Cpu,
    title: { de: "Hardware-Wallet", it: "Hardware wallet", en: "Hardware wallet" },
    intro: {
      de: "Unterstützung für Ledger-, Trezor- und andere Hardware-Wallets mit Fehlfunktionen, stets im Non-custodial-Modus.",
      it: "Assistenza per dispositivi Ledger, Trezor e altri hardware wallet con malfunzionamenti, sempre in modalità non-custodial.",
      en: "Support for Ledger, Trezor and other hardware wallets with malfunctions, always in non-custodial mode.",
    },
  },
  "eredita-digitale": {
    icon: Landmark,
    title: { de: "Digitaler Nachlass", it: "Eredità digitale", en: "Digital inheritance" },
    intro: {
      de: "Unterstützung für Erben und Fachleute (Anwälte, Notare) mit angemessener rechtlicher Dokumentation zum Nachweis der Berechtigung.",
      it: "Assistenza a eredi e professionisti (avvocati, notai) con documentazione legale adeguata a dimostrare l'autorità.",
      en: "Assistance for heirs and professionals (lawyers, notaries) with appropriate legal documentation to prove authority.",
    },
  },
};

// ---------------------------------------------------------------------------
// WALLETS (names are proper nouns; only the type category is translated)
// ---------------------------------------------------------------------------
const WALLETS_DATA = [
  { slug: "ledger", name: "Ledger", type: "hardware" },
  { slug: "trezor", name: "Trezor", type: "hardware" },
  { slug: "metamask", name: "MetaMask", type: "software" },
  { slug: "trust-wallet", name: "Trust Wallet", type: "software" },
  { slug: "blockchain-com", name: "Blockchain.com", type: "software" },
  { slug: "bitcoin-core", name: "Bitcoin Core", type: "software" },
  { slug: "electrum", name: "Electrum", type: "software" },
  { slug: "multibit", name: "MultiBit", type: "legacy" },
  { slug: "jaxx-liberty", name: "Jaxx Liberty", type: "software" },
  { slug: "exodus", name: "Exodus", type: "software" },
];

// ---------------------------------------------------------------------------
// WHY blocks
// ---------------------------------------------------------------------------
const WHY_DATA = [
  { icon: Lock,
    title: { de: "Vertraulichkeit und Sicherheit", it: "Riservatezza e sicurezza", en: "Confidentiality and security" },
    text: { de: "Kontrollierter Informationszugriff, Dokumentenverschlüsselung und Audit-Protokoll.", it: "Accesso controllato alle informazioni, cifratura dei documenti e registro di audit.", en: "Controlled information access, document encryption and audit log." } },
  { icon: SearchCheck,
    title: { de: "Individuelle technische Analyse", it: "Analisi tecnica personalizzata", en: "Tailored technical analysis" },
    text: { de: "Jeder Fall wird einzeln anhand der noch verfügbaren Unterlagen bewertet.", it: "Ogni caso è valutato singolarmente in base al materiale ancora disponibile.", en: "Each case is assessed individually based on the material still available." } },
  { icon: ShieldCheck,
    title: { de: "Transparenter Prozess", it: "Processo trasparente", en: "Transparent process" },
    text: { de: "Umfang, Autorisierung und Vergütung werden vor der Bearbeitung schriftlich festgelegt.", it: "Ambito, autorizzazione e compenso definiti per iscritto prima della lavorazione.", en: "Scope, authorization and fee defined in writing before work begins." } },
  { icon: MessagesSquare,
    title: { de: "Direkte Kommunikation", it: "Comunicazione diretta", en: "Direct communication" },
    text: { de: "Klare Statusaktualisierungen über das geschützte Portal.", it: "Aggiornamenti chiari sullo stato della pratica tramite il portale riservato.", en: "Clear status updates through the secure portal." } },
];

// ---------------------------------------------------------------------------
// HOW steps
// ---------------------------------------------------------------------------
const HOW_STEPS_DATA = [
  { n: "01",
    title: { de: "Vertrauliche Bewertung", it: "Valutazione riservata", en: "Confidential assessment" },
    text: { de: "Der Kunde beschreibt den Fall ohne Wallet-Geheimnisse. Wir erheben nur die für die Voranalyse nötigen Informationen.", it: "Il cliente descrive il caso senza fornire segreti del wallet. Raccogliamo solo le informazioni necessarie all'analisi preliminare.", en: "The client describes the case without providing wallet secrets. We collect only the information needed for the preliminary analysis." } },
  { n: "02",
    title: { de: "Prüfung und technische Analyse", it: "Verifica e analisi tecnica", en: "Verification and technical analysis" },
    text: { de: "Identität, Eigentum und technische Machbarkeit der Wiederherstellung werden anhand der verfügbaren Unterlagen geprüft.", it: "Vengono verificate identità, titolarità e fattibilità tecnica del recupero in base al materiale disponibile.", en: "Identity, ownership and technical feasibility of recovery are verified based on the available material." } },
  { n: "03",
    title: { de: "Bearbeitung und Übergabe", it: "Lavorazione e consegna", en: "Processing and delivery" },
    text: { de: "Nach schriftlicher Autorisierung wird der Fall kontrolliert bearbeitet. Bei wiederhergestelltem Zugriff unterstützen wir die Absicherung des Wallets.", it: "Dopo autorizzazione e accordo scritti, il caso è gestito con processo controllato. Se l'accesso viene ripristinato, assistiamo il cliente nella messa in sicurezza del wallet.", en: "After written authorization, the case is handled with a controlled process. If access is restored, we assist the client in securing the wallet." } },
];

// ---------------------------------------------------------------------------
// Simple string lists ({de,it,en})
// ---------------------------------------------------------------------------
const WHY_KEEP = WHY_DATA; // alias for clarity

const SECURITY_POINTS_DATA = [
  { de: "Non-custodial-Ansatz", it: "Approccio non-custodial", en: "Non-custodial approach" },
  { de: "Identitäts- und Eigentumsprüfung", it: "Verifica di identità e titolarità", en: "Identity and ownership verification" },
  { de: "Kontrollierter Zugriff auf Fallinformationen", it: "Accesso controllato alle informazioni del caso", en: "Controlled access to case information" },
  { de: "Dokumentenverschlüsselung", it: "Cifratura dei documenti", en: "Document encryption" },
  { de: "Audit-Protokoll", it: "Registro di audit", en: "Audit log" },
  { de: "Umfang und Autorisierung schriftlich", it: "Ambito e autorizzazione per iscritto", en: "Scope and authorization in writing" },
  { de: "Keine öffentliche Übermittlung privater Schlüssel oder vollständiger Seed-Phrasen", it: "Nessun invio pubblico di chiavi private o seed phrase complete", en: "No public sending of private keys or full seed phrases" },
];

const ELIGIBLE_DATA = [
  { de: "Vergessenes Passwort mit Teilhinweisen", it: "Password dimenticata con indizi parziali", en: "Forgotten password with partial clues" },
  { de: "Unvollständige Seed-Phrase", it: "Seed phrase incompleta", en: "Incomplete seed phrase" },
  { de: "Falsche Wortreihenfolge", it: "Ordine delle parole errato", en: "Wrong word order" },
  { de: "Ungültige Wiederherstellungsphrase", it: "Frase di recupero non valida", en: "Invalid recovery phrase" },
  { de: "Verschlüsseltes Wallet-Backup", it: "Backup del wallet cifrato", en: "Encrypted wallet backup" },
  { de: "Beschädigte Wallet-Datei", it: "File wallet corrotto", en: "Corrupted wallet file" },
  { de: "Legacy-Wallet-Software", it: "Software di wallet legacy", en: "Legacy wallet software" },
  { de: "Altes Gerät oder Hardware-Wallet", it: "Vecchio dispositivo o hardware wallet", en: "Old device or hardware wallet" },
  { de: "Digitaler Nachlass mit rechtlicher Dokumentation", it: "Eredità digitale con documentazione legale", en: "Digital inheritance with legal documentation" },
];

const NOT_SUPPORTED_DATA = [
  { de: "Wallets, die nicht dem Antragsteller gehören", it: "Wallet non appartenenti al richiedente", en: "Wallets not belonging to the applicant" },
  { de: "Anfragen ohne Eigentumsnachweis", it: "Richieste senza prova di titolarità", en: "Requests without proof of ownership" },
  { de: "Vollständig verlorene Seed-Phrase ohne Datei, Schlüssel, Backup oder Gerät", it: "Seed phrase completamente persa senza file, chiave, backup o dispositivo", en: "Fully lost seed phrase with no file, key, backup or device" },
  { de: "Rückgängigmachen von Blockchain-Transaktionen", it: "Annullamento di transazioni blockchain", en: "Reversing blockchain transactions" },
  { de: "\"Hack-back\"-Anfragen", it: "Richieste di tipo \"hack back\"", en: "\"Hack back\" requests" },
  { de: "Zugriff auf Konten Dritter", it: "Accesso ad account di terzi", en: "Access to third-party accounts" },
  { de: "Garantierte Wiederherstellung gestohlener Gelder", it: "Recupero garantito di fondi rubati", en: "Guaranteed recovery of stolen funds" },
];

const COMPLIANCE_DATA = [
  { de: "Wir arbeiten nur mit verifizierten Inhabern oder rechtlich autorisierten Personen.", it: "Lavoriamo solo con titolari verificati o soggetti legalmente autorizzati.", en: "We work only with verified owners or legally authorized parties." },
  { de: "Blockchain-Transaktionen können in der Regel nicht rückgängig gemacht werden.", it: "Le transazioni sulla blockchain non possono normalmente essere annullate.", en: "Blockchain transactions normally cannot be reversed." },
  { de: "Kein Wiederherstellungsergebnis ist garantiert.", it: "Nessun esito di recupero è garantito.", en: "No recovery outcome is guaranteed." },
  { de: "Die öffentliche Adresse allein reicht nicht aus, um ein Wallet wiederherzustellen.", it: "Il solo indirizzo pubblico non è sufficiente per recuperare un wallet.", en: "The public address alone is not enough to recover a wallet." },
  { de: "Eine vollständig verlorene Seed-Phrase ohne Backup, Datei, Schlüssel oder Gerät ist in der Regel nicht wiederherstellbar.", it: "Una seed phrase completamente persa, senza backup, file, chiave o dispositivo, è generalmente non recuperabile.", en: "A fully lost seed phrase, with no backup, file, key or device, is generally unrecoverable." },
  { de: "Der Dienst führt kein Hacking durch und greift nicht auf fremdes Eigentum zu.", it: "Il servizio non effettua hacking né accede a proprietà di terzi.", en: "The service does not perform hacking nor access third-party property." },
  { de: "Identität, Berechtigung und Eigentum können vor Annahme eines Falls geprüft werden.", it: "Identità, autorità e titolarità possono essere verificate prima di accettare un caso.", en: "Identity, authority and ownership may be verified before accepting a case." },
  { de: "Öffentliche Inhalte sind informativ und stellen keine Rechts- oder Finanzberatung dar.", it: "I contenuti pubblici sono informativi e non costituiscono consulenza legale o finanziaria.", en: "Public content is informational and does not constitute legal or financial advice." },
];

const BLOG_CATEGORIES_DATA = [
  { de: "Wallet-Wiederherstellung", it: "Recupero wallet", en: "Wallet recovery" },
  { de: "Passwortsicherheit", it: "Sicurezza password", en: "Password security" },
  { de: "Hardware-Wallet", it: "Hardware wallet", en: "Hardware wallet" },
  { de: "Legacy-Wallet", it: "Wallet legacy", en: "Legacy wallet" },
  { de: "Seed-Phrase-Wissen", it: "Educazione seed phrase", en: "Seed phrase education" },
  { de: "Krypto-Nachlass", it: "Eredità crypto", en: "Crypto inheritance" },
  { de: "Betrugsprävention", it: "Prevenzione truffe", en: "Fraud prevention" },
];

// ---------------------------------------------------------------------------
// Form option sets ({value,label:{de,it,en}})
// ---------------------------------------------------------------------------
const PROBLEM_TYPES_DATA = [
  { value: "forgotten_password", label: { de: "Vergessenes Passwort", it: "Password dimenticata", en: "Forgotten password" } },
  { value: "incomplete_seed", label: { de: "Unvollständige Seed-Phrase", it: "Seed phrase incompleta", en: "Incomplete seed phrase" } },
  { value: "wrong_order", label: { de: "Wörter in falscher Reihenfolge", it: "Parole nell'ordine sbagliato", en: "Words in the wrong order" } },
  { value: "invalid_seed", label: { de: "Ungültige Seed-Phrase", it: "Seed phrase non valida", en: "Invalid seed phrase" } },
  { value: "corrupted_file", label: { de: "Beschädigte Wallet-Datei", it: "File wallet corrotto", en: "Corrupted wallet file" } },
  { value: "legacy_wallet", label: { de: "Nicht unterstütztes Legacy-Wallet", it: "Wallet legacy non supportato", en: "Unsupported legacy wallet" } },
  { value: "hardware_problem", label: { de: "Hardware-Wallet-Problem", it: "Problema hardware wallet", en: "Hardware wallet problem" } },
  { value: "balance_not_visible", label: { de: "Guthaben nach Wiederherstellung nicht sichtbar", it: "Saldo non visibile dopo il ripristino", en: "Balance not visible after restore" } },
  { value: "derivation_path", label: { de: "Derivation-Path-Problem", it: "Problema di derivation path", en: "Derivation path problem" } },
  { value: "digital_inheritance", label: { de: "Digitaler Nachlass", it: "Eredità digitale", en: "Digital inheritance" } },
  { value: "other", label: { de: "Sonstiges", it: "Altro", en: "Other" } },
];

const MATERIAL_OPTIONS_DATA = [
  { value: "public_address", label: { de: "Öffentliche Wallet-Adresse", it: "Indirizzo pubblico del wallet", en: "Public wallet address" } },
  { value: "encrypted_file", label: { de: "Verschlüsselte Wallet-Datei", it: "File wallet cifrato", en: "Encrypted wallet file" } },
  { value: "hardware_device", label: { de: "Hardware-Gerät", it: "Dispositivo hardware", en: "Hardware device" } },
  { value: "partial_seed", label: { de: "Teil der Seed-Phrase", it: "Parte della seed phrase", en: "Part of the seed phrase" } },
  { value: "password_clues", label: { de: "Hinweise zum Passwort", it: "Indizi sulla password", en: "Password clues" } },
  { value: "old_device", label: { de: "Altes Gerät", it: "Vecchio dispositivo", en: "Old device" } },
  { value: "backup", label: { de: "Backup", it: "Backup", en: "Backup" } },
  { value: "purchase_records", label: { de: "Kaufbelege", it: "Documenti di acquisto", en: "Purchase records" } },
  { value: "transaction_evidence", label: { de: "Transaktionsnachweise", it: "Prove di transazioni", en: "Transaction evidence" } },
  { value: "legal_documents", label: { de: "Rechts- oder Nachlassdokumente", it: "Documenti legali o di eredità", en: "Legal or inheritance documents" } },
];

const CLIENT_TYPES_DATA = [
  { value: "individual", label: { de: "Privatperson", it: "Privato", en: "Individual" } },
  { value: "company", label: { de: "Unternehmen", it: "Azienda", en: "Company" } },
  { value: "heir", label: { de: "Erbe", it: "Erede", en: "Heir" } },
  { value: "lawyer", label: { de: "Anwalt", it: "Avvocato", en: "Lawyer" } },
  { value: "notary", label: { de: "Notar", it: "Notaio", en: "Notary" } },
  { value: "other", label: { de: "Anderer Fachmann", it: "Altro professionista", en: "Other professional" } },
];

const DOC_CATEGORIES_DATA = [
  { value: "identity_document", label: { de: "Ausweisdokument", it: "Documento d'identità", en: "Identity document" } },
  { value: "proof_of_address", label: { de: "Adressnachweis", it: "Prova di residenza", en: "Proof of address" } },
  { value: "proof_of_ownership", label: { de: "Eigentumsnachweis des Wallets", it: "Prova di titolarità del wallet", en: "Proof of wallet ownership" } },
  { value: "transaction_evidence", label: { de: "Transaktionsnachweis", it: "Prova di transazioni", en: "Transaction evidence" } },
  { value: "purchase_evidence", label: { de: "Kaufnachweis", it: "Prova d'acquisto", en: "Proof of purchase" } },
  { value: "inheritance_document", label: { de: "Nachlassdokumentation", it: "Documentazione di eredità", en: "Inheritance documentation" } },
  { value: "encrypted_wallet_file", label: { de: "Verschlüsselte Wallet-Datei", it: "File wallet cifrato", en: "Encrypted wallet file" } },
  { value: "other", label: { de: "Sonstiges autorisiertes Dokument", it: "Altro documento autorizzato", en: "Other authorized document" } },
];

// ---------------------------------------------------------------------------
// Locale-aware getters
// ---------------------------------------------------------------------------
export function getServices(lang = "de") {
  return SERVICES_DATA.map((s) => ({
    slug: s.slug, icon: s.icon,
    title: L(lang, s.title), short: L(lang, s.short),
    cases: s.cases.map((c) => L(lang, c)),
  }));
}

export function getServicePage(slug, lang = "de") {
  const p = SERVICE_PAGES_DATA[slug];
  if (!p) return null;
  return { icon: p.icon, title: L(lang, p.title), intro: L(lang, p.intro) };
}

export function getWallets(lang = "de") {
  return WALLETS_DATA.map((w) => ({ slug: w.slug, name: w.name, type: L(lang, WALLET_TYPES[w.type]) }));
}

export function getWhy(lang = "de") {
  return WHY_DATA.map((w) => ({ icon: w.icon, title: L(lang, w.title), text: L(lang, w.text) }));
}

export function getHowSteps(lang = "de") {
  return HOW_STEPS_DATA.map((s) => ({ n: s.n, title: L(lang, s.title), text: L(lang, s.text) }));
}

export function getSecurityPoints(lang = "de") { return SECURITY_POINTS_DATA.map((x) => L(lang, x)); }
export function getEligible(lang = "de") { return ELIGIBLE_DATA.map((x) => L(lang, x)); }
export function getNotSupported(lang = "de") { return NOT_SUPPORTED_DATA.map((x) => L(lang, x)); }
export function getCompliance(lang = "de") { return COMPLIANCE_DATA.map((x) => L(lang, x)); }
export function getBlogCategories(lang = "de") { return BLOG_CATEGORIES_DATA.map((x) => L(lang, x)); }

export function getProblemTypes(lang = "de") { return PROBLEM_TYPES_DATA.map((p) => ({ value: p.value, label: L(lang, p.label) })); }
export function getMaterialOptions(lang = "de") { return MATERIAL_OPTIONS_DATA.map((m) => ({ value: m.value, label: L(lang, m.label) })); }
export function getClientTypes(lang = "de") { return CLIENT_TYPES_DATA.map((c) => ({ value: c.value, label: L(lang, c.label) })); }
export function getDocCategories(lang = "de") { return DOC_CATEGORIES_DATA.map((d) => ({ value: d.value, label: L(lang, d.label) })); }

// Wallet name is a proper noun (language-independent).
export function walletName(slug) {
  const w = WALLETS_DATA.find((x) => x.slug === slug);
  return w ? w.name : slug;
}

export function problemLabel(value, lang = "de") {
  const p = PROBLEM_TYPES_DATA.find((x) => x.value === value);
  return p ? L(lang, p.label) : value;
}

export function docCategoryLabel(value, lang = "de") {
  const d = DOC_CATEGORIES_DATA.find((x) => x.value === value);
  return d ? L(lang, d.label) : value;
}
